'use client';
import { useEffect, useState } from 'react';

// 앱 웹뷰 호환성 판정.
//
// 왜 필요한가: 웹은 배포 즉시 모든 앱 버전에 반영되는데, 네이티브 수정은 스토어
// 업데이트를 받은 사람에게만 간다. 그래서 네이티브가 있어야 동작하는 기능을 웹에
// 그냥 열면, 업데이트하지 않은 사용자는 깨진 화면을 본다.
//
// 실제 사례가 마이룸이다. iOS 앱은 iframe 로드까지 "외부 도메인" 으로 판정해
// 별도 화면으로 보내버려서, 방이 앱 안에 뜨지 않고 부모-iframe postMessage(세션
// 주입·나가기)가 전부 끊겼다. 앱에서 고쳤지만 구버전 사용자에게는 여전하다.
//
// 판정 기준은 앱이 User-Agent 에 실어주는 계약 버전(JOYSAM-WV/N)이다. 버전
// 문자열(스토어 1.0.x vs 프로젝트 1.19.x)은 체계가 섞여 있어 대소 비교가 위험하다.
//   토큰 없음 = 이 값이 들어가기 전에 배포된 구버전 앱
//   2 이상    = 방 iframe 이 앱 안에서 정상 동작

// 방 iframe 이 앱 안에서 뜨려면 필요한 최소 계약 버전.
const MIN_WV_FOR_ROOM = 2;

interface AppInfo {
    inApp: boolean;      // 조이쌤 앱 웹뷰 안인가 (모바일 웹이면 false)
    isIOS: boolean;
    wv: number;          // 계약 버전. 토큰이 없으면 0
    build: number;       // 스토어 빌드 번호. 토큰이 없으면 0
    version: string;     // 스토어 표기 버전(1.0.0). 없으면 빈 문자열
}

// 앱이 UA 에 실어 보내는 스토어 버전: `JOYSAM-APP/<버전>(<빌드번호>)`.
//
// 비교는 **빌드 번호**로만 한다. 스토어 표기는 1.0.x 와 1.19.x 가 섞여 있어 대소
// 비교가 위험하지만(위 주석), 빌드 번호는 릴리스마다 반드시 증가한다.
//
// ⚠️ 버전 부분은 숫자와 점만이라고 가정하면 안 된다. 안드로이드 flavor 가
// versionName 을 "1.0-production" 으로 덮어서(android/app/build.gradle 의 product),
// 실서버 앱이 실제로 보내는 값은 `JOYSAM-APP/1.0-production(43)` 이다. 처음에
// [0-9.]+ 로 받았더니 안드로이드만 매칭에 실패해 build=0 으로 떨어졌고, 그 상태로
// 기준값을 올렸다면 **최신 앱 사용자까지 전부 차단**되고 업데이트해도 안 풀렸다.
// 버전 문자열은 표시용일 뿐이므로 괄호 앞까지 그대로 받는다.
//
// 이 토큰이 들어가기 전 빌드는 아무것도 안 보낸다 → build=0 으로 본다. 즉 "토큰 없음"
// 은 최신이 아니라 **가장 오래된 빌드**다. 반대로 읽으면 구버전이 게이트를 빠져나간다.
const APP_VERSION_RE = /JOYSAM-APP\/([^()\s]+)\((\d+)\)/;

export function getAppInfo(): AppInfo {
    if (typeof navigator === 'undefined') {
        // SSR 단계에서는 UA 를 못 본다. 화면을 막지 않도록 일반 웹으로 취급하고,
        // 클라이언트에서 다시 판정한다.
        return { inApp: false, isIOS: false, wv: 0, build: 0, version: '' };
    }
    const ua = navigator.userAgent || '';
    const inApp = ua.includes('JOYSAM');
    const matched = ua.match(/JOYSAM-WV\/(\d+)/);
    const app = ua.match(APP_VERSION_RE);
    return {
        inApp,
        isIOS: /iPhone|iPad|iPod/i.test(ua),
        wv: matched ? parseInt(matched[1], 10) : 0,
        build: app ? parseInt(app[2], 10) : 0,
        version: app ? app[1] : '',
    };
}

// 방에 들어가도 되는 환경인가.
//
// 모바일 웹은 제한하지 않는다. 앱은 플랫폼을 가리지 않고 계약 버전을 본다.
// 처음엔 iOS 앱만 막았는데(방 iframe 하이재킹), 안드로이드 구버전도 쓸 수 없다:
// 마이룸 소셜 연결은 연결 주소를 시스템 브라우저로 열고 딥링크로 돌아오는 네이티브
// 처리가 필요한데, 그 처리가 들어가기 전 빌드에는 양쪽 다 없다. 그런 앱에서는
// 연결 화면에서 앱으로 돌아오지 못한다(2026-09-13 확인).
export function canEnterRoom(): boolean {
    const { inApp, wv } = getAppInfo();
    if (!inApp) return true;
    return wv >= MIN_WV_FOR_ROOM;
}

// 마이룸 진입점(카드·배너·퀵메뉴·메뉴 항목)을 그려도 되는가.
//
// 눌렀을 때 막고 안내하는 것과 별개로, 쓸 수 없는 앱에서는 아예 보이지 않게 한다.
// 웹은 배포 즉시 모든 앱에 반영되므로, 실서버에 웹을 먼저 올려도 구버전 앱 사용자는
// 달라진 걸 느끼지 않는다 — 스토어 릴리스가 나가면 그때부터 보인다.
//
// 서버에서는 UA 를 못 보므로 첫 렌더는 지금과 같게 두고(허용) 화면에 붙은 뒤 판정한다.
// 그래야 하이드레이션이 어긋나지 않는다. 구버전 앱에서는 잠깐 보였다 사라진다.
export function useRoomEntryAllowed(): boolean {
    const [allowed, setAllowed] = useState(true);

    useEffect(() => {
        setAllowed(canEnterRoom());
    }, []);

    return allowed;
}

// 막혔을 때 사용자에게 보여줄 안내.
export const ROOM_UPDATE_NOTICE = {
    title: '앱 업데이트가 필요합니다',
    message: '마이룸은 최신 버전 앱에서 이용할 수 있습니다.\nApp Store에서 조이쌤 커넥트를 업데이트해주세요.',
};
